/**
 * THE WORKSPACE INTENT: every flat param the workspace spends once, held off the
 * address (launch-marketing-pages design M4; spec `public-marketing-pages` "One
 * workspace-intent one-shot carries every flat param the workspace spends once").
 *
 * A link into the workspace can carry an instruction the workspace acts on once:
 * a search (`q`), a record at a Focus section (the focus anchor), a My Account
 * group (the settings anchor). Left on the address, it would ride every later URL
 * write (the router's writes spread the current query) and act again on a reload
 * or a shell remount. So the router's one `beforeEach` moves every key of
 * `WORKSPACE_ONE_SHOT_PARAMS` off a workspace address into this store before any
 * shell mounts (`router/index.js`), and no shell reads one off the address. The
 * `tour` key rides no URL: the `/showcase/:key` record writes it (`setTour`,
 * design M9).
 *
 * THE LIFETIME.
 * - A consumer spends its own key: `take(key)` answers the value and clears it.
 * - An unconsumed key is dropped only when a navigation leaves the workspace:
 *   the guard calls `clear()` for any route that is not a workspace route.
 * - A navigation from one workspace location to another never drops it. A
 *   record's redirect into the workspace and the unified router's arrival
 *   `?state=` write both land before the lazy shell mounts, and the intent must
 *   still be there when it does.
 * - The mobile shell drops, at mount, the keys it has no surface for (`drop`).
 *
 * A LEAF on the boot allowlist: the router, which every public page boots with,
 * registers the guard, so this imports no store. The anchor names come from
 * `utils/deepLinkAnchors.js`, the one client spelling of the server's
 * `Inbox::DeepLink` params.
 */

import { ref } from "vue";
import { defineStore, acceptHMRUpdate } from "pinia";
import { FOCUS_ANCHOR_PARAMS, SETTINGS_ANCHOR_PARAM } from "@/utils/deepLinkAnchors";

// The keys one link writes together. The focus anchor is all three or nothing
// (`readFocusAnchor`), so a link naming any of them replaces the whole anchor,
// never mixing with an earlier link's, exactly as a new address would.
const ONE_SHOT_GROUPS = Object.freeze([
  Object.freeze(["q"]),
  Object.freeze(Object.values(FOCUS_ANCHOR_PARAMS)),
  Object.freeze([SETTINGS_ANCHOR_PARAM]),
]);

/** Every flat query key the workspace spends once: the guard's registry. */
export const WORKSPACE_ONE_SHOT_PARAMS = Object.freeze(ONE_SHOT_GROUPS.flat());

/** The key a route record writes and no URL carries (`setTour`, `take("tour")`). */
export const TOUR_INTENT_KEY = "tour";

const INTENT_KEYS = Object.freeze([...WORKSPACE_ONE_SHOT_PARAMS, TOUR_INTENT_KEY]);

// One query value as one string, as `URLSearchParams#get` (the anchor readers'
// reader) answers it: vue-router hands a repeated param back as an array, and a
// bare `?q` as null.
function firstValue(value) {
  return Array.isArray(value) ? (value[0] ?? null) : (value ?? null);
}

export const useWorkspaceIntentStore = defineStore("workspaceIntent", () => {
  const held = Object.fromEntries(INTENT_KEYS.map((key) => [key, ref(null)]));

  /**
   * Hold the one-shot keys a workspace address carried. A group the params name
   * replaces that group whole; every other held key is left as it is.
   *
   * @param {Object} params - a route's query
   */
  function put(params) {
    for (const group of ONE_SHOT_GROUPS) {
      if (!group.some((key) => key in params)) continue;
      for (const key of group) held[key].value = firstValue(params[key]);
    }
  }

  /** @param {Object|null} value - the tour a route record hands the workspace */
  function setTour(value) {
    held[TOUR_INTENT_KEY].value = value ?? null;
  }

  /**
   * Spend one key: its value, cleared as it is read, so it acts at most once.
   *
   * @param {string} key - a `WORKSPACE_ONE_SHOT_PARAMS` key or `"tour"`
   * @returns {*} the held value, or null
   */
  function take(key) {
    const slot = held[key];
    if (!slot) return null;
    const value = slot.value;
    slot.value = null;
    return value;
  }

  /** @param {string[]} keys - the keys a consumer with no surface for them drops */
  function drop(keys) {
    for (const key of keys) {
      if (held[key]) held[key].value = null;
    }
  }

  function clear() {
    drop(INTENT_KEYS);
  }

  return { ...held, put, setTour, take, drop, clear };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useWorkspaceIntentStore, import.meta.hot));
}
